import { useEffect, useRef, useState } from "react";
import { CaretDown, MapPin, MagnifyingGlass } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

function newSessionToken() {
  return (window.crypto?.randomUUID?.() || String(Math.random())).replace(/-/g, "");
}

// Free-text address input with a styled dropdown of the account's saved
// addresses plus US Google Places (New API) autocomplete suggestions.
export const AddressCombo = ({
  value,
  onChange,
  options = [],
  placeholder = "142 Elm St, Springfield",
  testId = "address-combo",
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [preds, setPreds] = useState([]);
  const [active, setActive] = useState(-1);
  const wrap = useRef(null);
  const debRef = useRef(null);
  const tokenRef = useRef(newSessionToken());
  const reqRef = useRef(0);
  const justSelected = useRef(false);

  const list = [...new Set(options.map((a) => (a || "").trim()).filter(Boolean))];
  const q = (value || "").trim().toLowerCase();
  const matches = list.filter((a) => !q || a.toLowerCase().includes(q));
  const shown =
    matches.length && !(matches.length === 1 && matches[0] === (value || "").trim())
      ? matches
      : list;

  const combined = [
    ...preds.map((p) => ({ value: p.text })),
    ...shown.map((a) => ({ value: a })),
  ];

  useEffect(() => {
    if (!open) return;
    const off = (e) => {
      if (!wrap.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", off);
    return () => document.removeEventListener("pointerdown", off);
  }, [open]);

  useEffect(() => () => debRef.current && clearTimeout(debRef.current), []);

  // Debounced Places (New) autocomplete fetch, US addresses only.
  const fetchPreds = (input) => {
    if (debRef.current) clearTimeout(debRef.current);
    const text = (input || "").trim();
    if (!MAPS_KEY || text.length < 3) {
      setPreds([]);
      return;
    }
    debRef.current = setTimeout(async () => {
      const myReq = ++reqRef.current;
      try {
        const res = await fetch("https://places.googleapis.com/v1/places:autocomplete", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": MAPS_KEY,
          },
          body: JSON.stringify({
            input: text,
            includedRegionCodes: ["us"],
            sessionToken: tokenRef.current,
          }),
        });
        if (myReq !== reqRef.current) return;
        if (!res.ok) {
          setPreds([]);
          return;
        }
        const data = await res.json();
        if (myReq !== reqRef.current) return;
        const out = (data.suggestions || [])
          .map((s) => s.placePrediction)
          .filter(Boolean)
          .map((p) => ({ id: p.placeId, text: p.text?.text || "" }))
          .filter((p) => p.text)
          .slice(0, 5);
        setPreds(out);
      } catch {
        setPreds([]);
      }
    }, 250);
  };

  const commit = (description) => {
    justSelected.current = true;
    reqRef.current++;
    onChange(description);
    setPreds([]);
    setOpen(false);
    tokenRef.current = newSessionToken();
  };

  return (
    <div ref={wrap} className={`relative ${className}`}>
      <Input
        data-testid={`${testId}-input`}
        value={value || ""}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v);
          setOpen(true);
          setActive(-1);
          if (justSelected.current) {
            justSelected.current = false;
          } else {
            fetchPreds(v);
          }
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setOpen(false);
            setActive(-1);
            return;
          }
          if (!open || !combined.length) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => (i + 1) % combined.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => (i <= 0 ? combined.length - 1 : i - 1));
          } else if (e.key === "Enter" && active >= 0 && active < combined.length) {
            e.preventDefault();
            commit(combined[active].value);
            setActive(-1);
          }
        }}
        placeholder={placeholder}
        autoComplete="off"
        className="h-12 border-zinc-800 bg-zinc-950 pr-10"
      />
      {list.length > 0 && (
        <button
          type="button"
          data-testid={`${testId}-toggle`}
          onClick={() => setOpen((o) => !o)}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-zinc-200"
          aria-label="Saved addresses"
        >
          <CaretDown
            size={14}
            weight="bold"
            className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      )}
      {open && (preds.length > 0 || shown.length > 0) && (
        <div
          data-testid={`${testId}-menu`}
          className="absolute left-0 right-0 top-full z-30 mt-1.5 overflow-hidden rounded-lg border border-white/[0.08] bg-zinc-900/95 p-1 shadow-[0_18px_40px_-16px_rgba(0,0,0,0.9)] backdrop-blur-xl"
        >
          {preds.length > 0 && (
            <>
              <div className="px-2.5 pb-1 pt-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                Address suggestions
              </div>
              {preds.map((p, i) => (
                <button
                  key={p.id || p.text}
                  type="button"
                  data-testid={`${testId}-suggestion`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => commit(p.text)}
                  className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-zinc-200 transition-colors duration-150 hover:bg-white/[0.07] ${
                    active === i ? "bg-white/[0.07]" : ""
                  }`}
                >
                  <MagnifyingGlass size={14} className="shrink-0 text-emerald-400" />
                  <span className="truncate">{p.text}</span>
                </button>
              ))}
            </>
          )}
          {shown.length > 0 && (
            <>
              <div className="px-2.5 pb-1 pt-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                Saved addresses
              </div>
              {shown.map((a, i) => (
                <button
                  key={a}
                  type="button"
                  data-testid={`${testId}-option`}
                  onMouseEnter={() => setActive(preds.length + i)}
                  onClick={() => {
                    justSelected.current = true;
                    onChange(a);
                    setPreds([]);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors duration-150 hover:bg-white/[0.07] ${
                    active === preds.length + i ? "bg-white/[0.07]" : ""
                  } ${a === value ? "text-amber-300" : "text-zinc-200"}`}
                >
                  <MapPin size={14} className="shrink-0 text-zinc-500" />
                  <span className="truncate">{a}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};
